<template>
    <el-image
        v-if="activeSrc"
        v-bind="$attrs"
        :src="activeSrc"
        :preview-src-list="previewSrcList"
        v-on="forwardedListeners"
        @load="handleLoad"
        @error="handleError"
    >
        <template slot="placeholder">
            <slot name="placeholder"></slot>
        </template>
        <template slot="error">
            <slot name="error"></slot>
        </template>
        <slot></slot>
    </el-image>
    <div v-else v-bind="$attrs" v-on="forwardedListeners">
        <slot name="placeholder"></slot>
        <slot></slot>
    </div>
</template>

<script>
class Semaphore {
    constructor(max) {
        this.max = max;
        this.current = 0;
        this.queue = [];
    }

    setMax(max) {
        if (!max || max < 1) {
            return;
        }
        this.max = max;
        this.drain();
    }

    acquire() {
        let entry = null;
        const promise = new Promise((resolve) => {
            entry = { resolve, canceled: false };
            if (this.current < this.max) {
                this.current += 1;
                resolve(this.makeRelease());
                return;
            }
            this.queue.push(entry);
        });

        const cancel = () => {
            if (entry) {
                entry.canceled = true;
            }
        };

        return { promise, cancel };
    }

    makeRelease() {
        let released = false;
        return () => {
            if (released) {
                return;
            }
            released = true;
            this.current = Math.max(0, this.current - 1);
            this.drain();
        };
    }

    drain() {
        while (this.current < this.max && this.queue.length) {
            const entry = this.queue.shift();
            if (!entry || entry.canceled) {
                continue;
            }
            this.current += 1;
            entry.resolve(this.makeRelease());
        }
    }
}

const globalImageSemaphore = new Semaphore(4);

export default {
    name: 'ConcurrencyImage',
    inheritAttrs: false,
    props: {
        src: String,
        previewSrcList: Array,
        max: {
            type: Number,
            default: 4
        },
        timeout: {
            type: Number,
            default: 20000
        }
    },
    data() {
        return {
            activeSrc: null,
            desiredSrc: this.src,
            cancelAcquire: null,
            release: null,
            loadTimeoutId: null,
            loadingSrc: null,
            acquireSeq: 0
        }
    },
    computed: {
        forwardedListeners() {
            const listeners = this.$listeners || {};
            const forwarded = {};
            Object.keys(listeners).forEach((key) => {
                if (key === 'load' || key === 'error') {
                    return;
                }
                forwarded[key] = listeners[key];
            });
            return forwarded;
        }
    },
    watch: {
        src: {
            immediate: true,
            handler(val) {
                // console.log('[ConcurrencyImage] src:', val);
                this.desiredSrc = val;
                this.resetAndAcquire();
            }
        },
        max(val) {
            globalImageSemaphore.setMax(val);
        }
    },
    mounted() {
        globalImageSemaphore.setMax(this.max);
    },
    beforeDestroy() {
        this.cleanup();
    },
    methods: {
        clearLoadTimeout() {
            if (this.loadTimeoutId) {
                clearTimeout(this.loadTimeoutId);
                this.loadTimeoutId = null;
            }
            this.loadingSrc = null;
        },
        startLoadTimeout(src) {
            this.clearLoadTimeout();
            const ms = Number(this.timeout);
            if (!ms || ms <= 0) {
                return;
            }
            this.loadingSrc = src;
            this.loadTimeoutId = setTimeout(() => {
                this.loadTimeoutId = null;
                const stillSame = this.loadingSrc && this.activeSrc === this.loadingSrc;
                if (!stillSame) {
                    return;
                }
                this.clearLoadTimeout();
                this.finish();
                this.$emit('error', new Error('Image load timeout'));
            }, ms);
        },
        cleanup() {
            this.clearLoadTimeout();
            if (this.cancelAcquire) {
                this.cancelAcquire();
                this.cancelAcquire = null;
            }
            if (this.release) {
                const release = this.release;
                this.release = null;
                release();
            }
        },
        resetAndAcquire() {
            this.cleanup();
            this.activeSrc = null;
            const seq = ++this.acquireSeq;
            this.$nextTick(() => {
                if (this._isDestroyed || this._destroyed) {
                    return;
                }
                if (seq !== this.acquireSeq) {
                    return;
                }
                this.acquireAndStart(seq);
            });
        },
        acquireAndStart(seq) {
            if (!this.desiredSrc) {
                this.activeSrc = null;
                return;
            }
            const ticket = globalImageSemaphore.acquire();
            const cancelAcquire = ticket.cancel;
            this.cancelAcquire = cancelAcquire;
            ticket.promise.then((release) => {
                if (this._isDestroyed || this._destroyed) {
                    release();
                    return;
                }
                if (seq !== this.acquireSeq) {
                    release();
                    return;
                }
                if (this.cancelAcquire === cancelAcquire) {
                    this.cancelAcquire = null;
                }
                if (this.release) {
                    const prevRelease = this.release;
                    this.release = null;
                    prevRelease();
                }
                this.release = release;
                const resolvedSrc = this.desiredSrc;
                if (!resolvedSrc) {
                    this.activeSrc = null;
                    this.finish();
                    return;
                }
                this.activeSrc = resolvedSrc;
                this.startLoadTimeout(resolvedSrc);
            });
        },
        finish() {
            this.clearLoadTimeout();
            if (!this.release) {
                return;
            }
            const release = this.release;
            this.release = null;
            release();
        },
        handleLoad(e) {
            if (!this.activeSrc) {
                this.finish();
                return;
            }
            this.finish();
            this.$emit('load', e);
        },
        handleError(e) {
            if (!this.activeSrc) {
                this.finish();
                return;
            }
            this.finish();
            this.$emit('error', e);
        }
    }
}
</script>
